import { stageDefFromStand, stageMaxPoints } from './ipscScoring';

function normalizePositiveInteger(value) {
    const number = parseInt(value, 10);
    return Number.isFinite(number) && number > 0 ? number : null;
}

function formatStoredDistance(value) {
    if (value === undefined || value === null || value === '') {
        return null;
    }
    const text = String(value).trim();
    if (!text) {
        return null;
    }
    return /^\d+(?:[.,]\d+)?$/.test(text) ? `${text} meter` : text;
}

export function splitStandDistanceAndNotes(stand = {}) {
    const storedDistance = formatStoredDistance(
        stand.distance || stand.distanceMeters || stand.distanceToTarget
    );
    const notes = String(stand.notes || '').trim();
    if (storedDistance || !notes) {
        return { distance: storedDistance, notes };
    }

    const match = notes.match(/\bavstand\s*:?\s*([^.\n]+)\.?/i);
    if (!match) {
        return { distance: null, notes };
    }

    const remainingNotes = `${notes.slice(0, match.index)} ${notes.slice(match.index + match[0].length)}`
        .replace(/\s+/g, ' ')
        .replace(/^[\s.,;:-]+|[\s,;:-]+$/g, '')
        .trim();

    return {
        distance: match[1].trim(),
        notes: remainingNotes,
    };
}

export function buildStandCourseSlides(convention = {}) {
    const stands = Array.isArray(convention.stageStands) ? convention.stageStands : [];
    const images = Array.isArray(convention.stageStandImages) ? convention.stageStandImages : [];

    return images.reduce((slides, image, idx) => {
        if (!image || typeof image !== 'object') {
            return slides;
        }
        const standNumber = idx + 1;
        const stand = stands.find(item => normalizePositiveInteger(item?.stand) === standNumber) || {};
        slides.push({
            image: { ...image },
            stand: { ...stand },
            standNumber,
        });
        return slides;
    }, []);
}

export function getStandCourseFacts(stand = {}) {
    const { distance, notes } = splitStandDistanceAndNotes(stand);
    return {
        distance,
        notes,
        shootingPosition: String(stand.shootingPosition || '').trim(),
        shootingTime: normalizePositiveInteger(stand.shootingTime),
        scoreGroups: normalizePositiveInteger(stand.scoreGroups),
        scoreTargets: normalizePositiveInteger(stand.scoreTargets),
        scoreHits: normalizePositiveInteger(stand.scoreHits),
        scoreInner: normalizePositiveInteger(stand.scoreInner),
        // IPSC keys only when the stand carries an IPSC definition (NSF stands unchanged).
        ...(getStandIpscFacts(stand).ipsc ? getStandIpscFacts(stand) : {}),
    };
}

// IPSC (dynamisk) stage facts from the ipsc* keys on a stageStands entry; null
// members when the stand carries no IPSC definition.
export function getStandIpscFacts(stand = {}) {
    const hasIpsc = ['ipscPaperTargets', 'ipscPoppers', 'ipscPlates', 'ipscNoShoots', 'ipscMinRounds', 'ipscBrief']
        .some(key => stand[key] !== undefined && stand[key] !== null && stand[key] !== '');
    if (!hasIpsc) {
        return { ipsc: null, ipscMax: null, ipscBrief: '' };
    }
    const def = stageDefFromStand(stand);
    return {
        ipsc: def,
        ipscMax: stageMaxPoints(def),
        ipscBrief: String(stand.ipscBrief || '').trim(),
    };
}

// Poster line, e.g. "IPSC: 4 PAPER × 2, 2 STEEL, 1 NO-SHOOT, MIN 10 ROUNDS, MAX 50 POINTS".
export function formatIpscStageLine(facts) {
    if (!facts || !facts.ipsc) {
        return null;
    }
    const d = facts.ipsc;
    const steel = d.poppers + d.plates;
    return `IPSC: ${d.paper} PAPER × ${d.hitsPerPaper}, ${steel} STEEL, ${d.noShoots} NO-SHOOT, MIN ${d.minRounds} ROUNDS, MAX ${facts.ipscMax} POINTS`;
}

// Magnum classes often get their own shooting position, time and extra
// instructions on a stand (e.g. "Stående 2 hender, 18 sekunder, Magnum skyter
// ikke på stålet"), stored on the same stageStands entry as
// magnumShootingPosition / magnumShootingTime / magnumNotes. Null when none is set.
export function getStandMagnumFacts(stand = {}) {
    const shootingTime = normalizePositiveInteger(stand?.magnumShootingTime);
    const shootingPosition = String(stand?.magnumShootingPosition || '').trim();
    const notes = String(stand?.magnumNotes || '').trim();
    if (shootingTime == null && !shootingPosition && !notes) {
        return null;
    }
    return { shootingTime, shootingPosition, notes };
}

// Poster line, e.g. "MAGNUM: STÅENDE 2 HENDER, 18 SEKUNDER, MAGNUM SKYTER IKKE PÅ STÅLET".
export function formatStandMagnumLine(facts) {
    if (!facts) {
        return null;
    }
    const parts = [];
    if (facts.shootingPosition) parts.push(facts.shootingPosition.toUpperCase());
    if (facts.shootingTime != null) parts.push(`${facts.shootingTime} SEKUNDER`);
    if (facts.notes) parts.push(facts.notes.toUpperCase());
    return parts.length ? `MAGNUM: ${parts.join(', ')}` : null;
}

// The stand-specific lines of the instruction-card script (on screen and
// printed). Missing standard values become a write-in blank. Magnum values are
// added inline in parentheses, e.g.
//   SKYTESTILLING ER: STÅENDE FRI FOR ALLE. (MAGNUM 2 HENDER).
//   SKYTETID ER 12 SEKUNDER. (MAGNUM 18 SEKUNDER).
// and magnum extra info gets its own line after the zones line.
export function buildStandScriptLines(values = {}, magnum = null) {
    const { shootingTime, shootingPosition, notes, scoreTargets, scoreGroups, scoreInner } = values;
    const blank = '________';
    const lines = [];

    let positionLine = `SKYTESTILLING ER: ${shootingPosition ? shootingPosition.toUpperCase() : blank}`;
    if (magnum?.shootingPosition) {
        positionLine += ` (MAGNUM ${magnum.shootingPosition.toUpperCase()}).`;
    }
    lines.push(positionLine);

    let timeLine = `SKYTETID ER ${shootingTime != null ? shootingTime : blank} SEKUNDER.`;
    if (magnum?.shootingTime != null) {
        timeLine += ` (MAGNUM ${magnum.shootingTime} SEKUNDER).`;
    }
    lines.push(timeLine);

    const groups = scoreGroups != null ? scoreGroups : 1;
    const groupsText = `${scoreGroups != null ? scoreGroups : blank} MÅLGRUPPE${groups > 1 ? 'R' : ''}`;
    let targetsLine = `HER ER DET ${groupsText} MED ${scoreTargets != null ? scoreTargets : blank} MÅL.`;
    if (notes) {
        targetsLine += ` ${notes.toUpperCase()}`;
    }
    lines.push(targetsLine);

    if (scoreInner != null) {
        lines.push('SONER TELLES.');
    }
    if (magnum?.notes) {
        lines.push(`MAGNUM: ${magnum.notes.toUpperCase().replace(/[.!]*$/, '.')}`);
    }
    return lines;
}

// Compact on-screen summary for badges and admin strips, e.g.
// "Stående 2 hender, 18s, Magnum skyter ikke på stålet". Empty string for null.
export function formatStandMagnumSummary(facts) {
    if (!facts) {
        return '';
    }
    return [
        facts.shootingPosition,
        facts.shootingTime != null ? `${facts.shootingTime}s` : '',
        facts.notes,
    ].filter(Boolean).join(', ');
}

// Same as getStandMagnumFacts, but looked up by stand number on the
// convention — for the admin views that only hold the stand number.
export function getStandMagnumInfo(convention = {}, stand) {
    const standNumber = normalizePositiveInteger(stand);
    const stands = Array.isArray(convention?.stageStands) ? convention.stageStands : [];
    const entry = stands.find(item => normalizePositiveInteger(item?.stand) === standNumber);
    return entry ? getStandMagnumFacts(entry) : null;
}

// Shooting time (seconds) the embedded Skytetrener should use for a stand.
// Regular shooters shoot first, then the magnum shooters right after; the
// scoring page toggles `magnum` to switch the trainer to the stand's magnum
// time. Falls back to the regular time when no magnum time is set, and to
// null when the stand has no regular time either.
export function getStandTrainerShootingTime(convention = {}, stand, { magnum = false } = {}) {
    const standNumber = normalizePositiveInteger(stand);
    const stands = Array.isArray(convention?.stageStands) ? convention.stageStands : [];
    const entry = stands.find(item => normalizePositiveInteger(item?.stand) === standNumber);
    if (!entry) {
        return null;
    }
    if (magnum) {
        const magnumTime = normalizePositiveInteger(entry.magnumShootingTime);
        if (magnumTime != null) {
            return magnumTime;
        }
    }
    return normalizePositiveInteger(entry.shootingTime);
}
